/* eslint-disable sort-exports/sort-exports */
import { atom } from "jotai";
import { z } from "zod";

import { JotaiTool } from "../../index";

export const StepTestingParams = z.object({
  zap_id: z
    .string()
    .describe("The unique identifier for the Zap containing the step to test"),
  step_id: z.string().describe("The ID of the specific step to test"),
  account_id: z
    .string()
    .describe(
      "The account ID that owns the Zap. This MUST be sourced from 'additionalData.canvas.account_id'. Do NOT ask the user for this value.",
    ),
  origin: z
    .string()
    .optional()
    .default("main")
    .describe(
      "Optional: The origin context for the test (e.g., 'main', 'editor'). Defaults to 'main'.",
    ),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Testing step...' or 'Running workflow test...'",
    ),
});

export type StepTestingParamsType = z.infer<typeof StepTestingParams>;

export const stepTestingExecute = async (rawArgs: any) => {
  let params: StepTestingParamsType;
  try {
    params = StepTestingParams.parse(rawArgs || {});
  } catch (error) {
    console.error("Error parsing arguments for stepTesting:", error);
    return {
      $error: {
        message: `Error: Invalid arguments provided for step_testing. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
      },
    };
  }

  const { zap_id, step_id, account_id, origin } = params;

  const query = new URLSearchParams({
    account_id,
    origin: origin || "main",
  });
  const proxyApiUrl = `/api/gulliver/steptesting/v2/zaps/${zap_id}/steps/${step_id}/output/test/run?${query.toString()}`;

  try {
    const csrfToken =
      window.document.cookie
        .split("; ")
        .find((row) => row.startsWith("csrftoken="))
        ?.split("=")[1] || "unknown";
    const res = await fetch(proxyApiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "x-csrftoken": csrfToken,
      },
    });

    if (!res.ok) {
      let errorDetailString = `Proxy error for step_testing (${res.status}).`;
      let responseDataForLog: any =
        "[Could not read error body after initial attempt]";
      try {
        const errorData = await res.json();
        responseDataForLog = errorData;
        if (errorData && errorData.error) {
          errorDetailString = `Proxy error for step_testing (${res.status}): ${errorData.error}${errorData.details ? ": " + JSON.stringify(errorData.details) : ""}`;
        } else {
          errorDetailString = `Proxy error for step_testing (${res.status}): Received non-standard JSON error from proxy: ${JSON.stringify(errorData)}`;
        }
      } catch {
        errorDetailString = `Proxy error for step_testing (${res.status}): Proxy error response was not valid JSON.`;
      }
      console.error(
        `[STEP_TESTING_TOOL] Proxy error (${res.status}) for URL ${proxyApiUrl}. Details: ${JSON.stringify(responseDataForLog)}`,
      );
      throw new Error(errorDetailString);
    }

    try {
      const data = await res.json();
      return {
        content: [
          {
            type: "text",
            text: JSON.stringify(data, null, 2),
          },
        ],
      };
    } catch (e) {
      console.error(
        `[STEP_TESTING_TOOL] Error parsing successful proxy response as JSON. URL: ${proxyApiUrl}, Status: ${res.status}. Error: ${e instanceof Error ? e.message : String(e)}`,
      );
      throw new Error(
        `Proxy returned success status (${res.status}) but its response body was not valid JSON.`,
      );
    }
  } catch (error) {
    console.error("[STEP_TESTING_TOOL] Error in stepTestingExecute:", error);
    return {
      $error: {
        message: `Error testing step: ${error instanceof Error ? error.message : String(error)}`,
      },
    };
  }
};

export const stepTestingDescription = `Tests the execution of a specific step in a Zap to verify it works correctly with the current configuration.
This tool runs a test of the step's action and returns the results, which can help diagnose issues or verify that a step is properly configured.

IMPORTANT:
- This tool tests a step in its current state with its current configuration.
- If you need information about the step's configuration first, use the 'get_zap' tool to retrieve the full Zap structure.
- The test results will show whether the step executed successfully and provide any output data or error messages.
- This is useful for debugging step configuration issues or verifying that a step works as expected.

Required parameters:
- zap_id: The ID of the Zap containing the step to test.
    - If the user mentions a Zap by name on the canvas, try to find its ID in 'additionalData.canvas.nodes' (look for a node with a matching label and use its 'data.zapId' or 'data.zap_id' property).
    - If the Zap ID is directly provided or known, use that.
- step_id: The ID of the specific step within the Zap to test.
    - This can be obtained from the 'get_zap' tool response in the 'steps' array, where each step has an 'id' field.
- account_id: The ID of the account that owns the Zap.
    - This MUST be sourced from 'additionalData.canvas.account_id'. Do NOT ask the user for this value.

Optional parameters:
- origin: The context from which the test is being run (defaults to 'main').

Output:
- The tool returns a JSON object with the test results, including success status, output data, and any error messages.
- Successful tests will typically include the data that the step would output when running in a live Zap.
- Failed tests will include error details to help diagnose configuration issues.`;

export const stepTestingAtom = atom<
  JotaiTool<z.infer<typeof StepTestingParams>>
>(() => ({
  name: "step_testing",
  description: stepTestingDescription,
  parameters: StepTestingParams,
  execute: atom(null, async (_get, _set, { args }) => {
    return stepTestingExecute(args);
  }),
}));
stepTestingAtom.debugLabel = "stepTestingAtom";

/* eslint-enable sort-exports/sort-exports */
